import React, { useState, useEffect, useMemo } from "react";
import {
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaTools,
  FaServer,
} from "react-icons/fa";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import dayjs from "dayjs";
import { getChecklistTemplate } from "./checklistTemplates";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

function AddPerawatanIt({
  isModalOpen,
  setIsModalOpen,
  form,
  setForm,
  postData,
  isEditMode,
  resetForm,
  kategoriOptions = [],
  layananOptions = [],
  inventarisOptions = [],
  loadingOpsi = false,
  onKategoriChange,
  onInventarisSearch,
}) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isModalOpen]);

  const selectedKategori = kategoriOptions.find(
    (k) => String(k.id) === String(form.kategori_id)
  );
  const tipe = selectedKategori?.tipe || "keduanya";
  const showLayanan = tipe === "layanan" || tipe === "keduanya";
  const showAset = tipe === "fisik" || tipe === "keduanya";

  const statusOptions = [
    { label: "Dicek", value: "dicek" },
    { label: "Bermasalah", value: "bermasalah" },
    { label: "Selesai", value: "selesai" },
  ];

  const inventarisDropdown = useMemo(
    () =>
      inventarisOptions.map((a) => ({
        label: `${a.kode || "-"}${a.kode_ypt ? ` / ${a.kode_ypt}` : ""} — ${a.desc || a.merk || "-"}${a.ruang ? ` (${a.ruang})` : ""}`,
        value: a.id,
      })),
    [inventarisOptions]
  );

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleKategoriSelect = (value) => {
    const kat = kategoriOptions.find((k) => String(k.id) === String(value));
    setForm((prev) => ({
      ...prev,
      kategori_id: value,
      id_layanan: "",
      inventaris_id: "",
      aset_manual_nama: "",
      aset_manual_lokasi: "",
      sumber_aset: "inventaris",
      checklist: getChecklistTemplate(kat?.kode),
    }));
    onKategoriChange?.(value);
  };

  const setSumberAset = (value) => {
    setForm((prev) => ({
      ...prev,
      sumber_aset: value,
      inventaris_id: value === "inventaris" ? prev.inventaris_id : "",
      aset_manual_nama: value === "manual" ? prev.aset_manual_nama : "",
      aset_manual_lokasi: value === "manual" ? prev.aset_manual_lokasi : "",
    }));
  };

  const updateChecklistItem = (index, field, value) => {
    setForm((prev) => {
      const list = [...(prev.checklist || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, checklist: list };
    });
  };

  const addChecklistRow = () => {
    setForm((prev) => ({
      ...prev,
      checklist: [
        ...(prev.checklist || []),
        { item: "", status: null, nilai: "" },
      ],
    }));
  };

  const removeChecklistRow = (index) => {
    setForm((prev) => ({
      ...prev,
      checklist: (prev.checklist || []).filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.tanggal) next.tanggal = "Tanggal wajib diisi";
    if (!form.kategori_id) next.kategori_id = "Kategori wajib dipilih";

    const hasInv = !!form.inventaris_id;
    const hasManual = !!(form.aset_manual_nama && String(form.aset_manual_nama).trim());
    const hasLayanan = !!form.id_layanan;
    if (!hasInv && !hasManual && !hasLayanan) {
      next.target = "Pilih inventaris, isi aset manual, atau pilih layanan";
    }
    if (showAset && form.sumber_aset === "inventaris" && !hasInv && !hasLayanan) {
      next.target = "Pilih aset dari inventaris atau ganti ke input manual";
    }
    if (showAset && form.sumber_aset === "manual" && !hasManual && !hasLayanan) {
      next.target = "Isi nama aset manual";
    }
    if (!form.status) next.status = "Status wajib dipilih";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await postData();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm?.();
    setErrors({});
    setIsModalOpen(false);
  };

  const header = (
    <div className="flex items-center gap-3">
      <div className="p-2 bg-primary/10 rounded-lg">
        <FaServer className="text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 m-0">
          {isEditMode ? "Edit Perawatan IT" : "Input Perawatan IT"}
        </h3>
        <p className="text-sm text-gray-500 m-0">
          Inventaris jika sudah tercatat, atau isi manual jika belum
        </p>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        label="Batal"
        icon="pi pi-times"
        className="p-button-text"
        onClick={handleClose}
        disabled={isSubmitting}
      />
      <Button
        label={isEditMode ? "Update" : "Simpan"}
        icon="pi pi-save"
        onClick={handleSubmit}
        loading={isSubmitting}
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => {
          resetForm?.();
          setIsModalOpen(true);
        }}
        className="flex items-center gap-2 bg-primary text-white px-4 py-3 rounded-xl hover:opacity-90 transition-all shadow-lg"
      >
        <FaPlus />
        <span className="font-medium">Tambah Perawatan</span>
      </button>

      <Dialog
        visible={isModalOpen}
        onHide={handleClose}
        header={header}
        footer={footer}
        style={{ width: "95vw", maxWidth: "720px" }}
        modal
        draggable={false}
        resizable={false}
        appendTo={document.body}
        baseZIndex={9999}
      >
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FaCalendarAlt className="inline mr-1" /> Tanggal
              </label>
              <Calendar
                value={form.tanggal ? new Date(form.tanggal) : null}
                onChange={(e) =>
                  handleChange(
                    "tanggal",
                    e.value ? dayjs(e.value).format("YYYY-MM-DD") : ""
                  )
                }
                dateFormat="dd/mm/yy"
                showIcon
                className="w-full"
              />
              {errors.tanggal && (
                <p className="text-red-500 text-xs mt-1">{errors.tanggal}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Petugas
              </label>
              <InputText
                value={form.nama_petugas || ""}
                onChange={(e) => handleChange("nama_petugas", e.target.value)}
                className="w-full"
                disabled
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategori IT
            </label>
            <Dropdown
              value={
                form.kategori_id
                  ? Number(form.kategori_id) || form.kategori_id
                  : null
              }
              options={kategoriOptions.map((k) => ({
                label: `${k.nama} (${k.tipe})`,
                value: k.id,
              }))}
              onChange={(e) => handleKategoriSelect(e.value)}
              placeholder={loadingOpsi ? "Memuat..." : "Pilih kategori"}
              className="w-full"
              filter
            />
            {errors.kategori_id && (
              <p className="text-red-500 text-xs mt-1">{errors.kategori_id}</p>
            )}
          </div>

          {showLayanan && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Layanan
              </label>
              <Dropdown
                value={
                  form.id_layanan
                    ? Number(form.id_layanan) || form.id_layanan
                    : null
                }
                options={layananOptions.map((l) => ({
                  label: `${l.nama_layanan}${l.penyedia ? ` — ${l.penyedia}` : ""}`,
                  value: l.id,
                }))}
                onChange={(e) => handleChange("id_layanan", e.value)}
                placeholder="Pilih layanan (opsional jika isi aset)"
                className="w-full"
                showClear
                filter
                disabled={!form.kategori_id}
              />
            </div>
          )}

          {showAset && (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
              <p className="text-sm font-semibold text-gray-800 m-0">
                Sumber aset fisik
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <RadioButton
                    inputId="sumber-inv"
                    name="sumber_aset"
                    value="inventaris"
                    onChange={() => setSumberAset("inventaris")}
                    checked={form.sumber_aset === "inventaris"}
                  />
                  <label htmlFor="sumber-inv" className="text-sm">
                    Dari inventaris
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioButton
                    inputId="sumber-manual"
                    name="sumber_aset"
                    value="manual"
                    onChange={() => setSumberAset("manual")}
                    checked={form.sumber_aset === "manual"}
                  />
                  <label htmlFor="sumber-manual" className="text-sm">
                    Input manual
                  </label>
                </div>
              </div>

              {form.sumber_aset === "inventaris" ? (
                <div>
                  <Dropdown
                    value={
                      form.inventaris_id
                        ? Number(form.inventaris_id) || form.inventaris_id
                        : null
                    }
                    options={inventarisDropdown}
                    onChange={(e) => handleChange("inventaris_id", e.value)}
                    placeholder="Cari / pilih inventaris..."
                    className="w-full"
                    filter
                    filterBy="label"
                    showClear
                    onFilter={(e) => onInventarisSearch?.(e.filter)}
                    emptyFilterMessage="Tidak ditemukan"
                    emptyMessage={
                      loadingOpsi ? "Memuat..." : "Ketik untuk mencari aset"
                    }
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Nama aset
                    </label>
                    <InputText
                      value={form.aset_manual_nama || ""}
                      onChange={(e) =>
                        handleChange("aset_manual_nama", e.target.value)
                      }
                      placeholder="Contoh: CCTV Lobby"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Lokasi
                    </label>
                    <InputText
                      value={form.aset_manual_lokasi || ""}
                      onChange={(e) =>
                        handleChange("aset_manual_lokasi", e.target.value)
                      }
                      placeholder="Contoh: Lobby"
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {errors.target && (
            <p className="text-red-500 text-xs -mt-2">{errors.target}</p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Dropdown
              value={form.status}
              options={statusOptions}
              onChange={(e) => handleChange("status", e.value)}
              className="w-full"
            />
            {errors.status && (
              <p className="text-red-500 text-xs mt-1">{errors.status}</p>
            )}
          </div>

          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2 m-0">
                <FaTools className="text-primary" /> Checklist
              </h4>
              <button
                type="button"
                onClick={addChecklistRow}
                className="text-sm text-primary hover:underline"
              >
                + Item
              </button>
            </div>
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {(form.checklist || []).map((row, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg border border-gray-100 p-3 space-y-2"
                >
                  <div className="flex gap-2">
                    <InputText
                      value={row.item || ""}
                      onChange={(e) =>
                        updateChecklistItem(idx, "item", e.target.value)
                      }
                      placeholder="Item pengecekan"
                      className="w-full"
                    />
                    <button
                      type="button"
                      onClick={() => removeChecklistRow(idx)}
                      className="filter-icon-btn flex h-8 w-8 shrink-0 items-center justify-center text-red-400 hover:text-red-600"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <RadioButton
                        inputId={`ok-${idx}`}
                        name={`status-${idx}`}
                        value="ok"
                        onChange={() =>
                          updateChecklistItem(idx, "status", "ok")
                        }
                        checked={row.status === "ok"}
                      />
                      <label htmlFor={`ok-${idx}`} className="text-sm">
                        OK
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioButton
                        inputId={`tidak-${idx}`}
                        name={`status-${idx}`}
                        value="tidak"
                        onChange={() =>
                          updateChecklistItem(idx, "status", "tidak")
                        }
                        checked={row.status === "tidak"}
                      />
                      <label htmlFor={`tidak-${idx}`} className="text-sm">
                        Tidak
                      </label>
                    </div>
                    <InputText
                      value={row.nilai || ""}
                      onChange={(e) =>
                        updateChecklistItem(idx, "nilai", e.target.value)
                      }
                      placeholder="Nilai / ket"
                      className="flex-1 min-w-[120px]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catatan
            </label>
            <InputTextarea
              value={form.catatan || ""}
              onChange={(e) => handleChange("catatan", e.target.value)}
              rows={3}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Foto dokumentasi
            </label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) =>
                handleChange("foto", e.target.files?.[0] || null)
              }
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary/10 file:text-primary"
            />
          </div>

          {form.status === "bermasalah" && form.inventaris_id && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  inputId="buat_perbaikan"
                  checked={!!form.buat_perbaikan}
                  onChange={(e) => handleChange("buat_perbaikan", e.checked)}
                />
                <label htmlFor="buat_perbaikan" className="text-sm font-medium">
                  Buat tindak lanjut perbaikan aset
                </label>
              </div>
              {form.buat_perbaikan && (
                <>
                  <InputText
                    value={form.jenis_kerusakan || ""}
                    onChange={(e) =>
                      handleChange("jenis_kerusakan", e.target.value)
                    }
                    placeholder="Jenis kerusakan"
                    className="w-full"
                  />
                  <InputTextarea
                    value={form.tindakan || ""}
                    onChange={(e) => handleChange("tindakan", e.target.value)}
                    placeholder="Tindakan awal"
                    rows={2}
                    className="w-full"
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}

export default AddPerawatanIt;
